import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "../../../../components/common/Toast/toast";
import { deleteClassroom } from "../../../../../services/ClassroomService.js";
import { getLabsList } from "../../../../../services/ClassroomManagementService.js";
import { useAuth } from "../../../../../context/AuthContext.js";
import NewCustomDataTable from "../../../../components/common/NewCustomDataTable.jsx";
import useScrollLoadMore from "../../../../components/common/useScrollLoadMore";
import useDebouncedCallback from "../../../../components/common/useDebouncedCallback";
import LabFormModal from "./LabFormModal.jsx";
import PopupDone, { usePopupDone } from "../../../../components/common/PopupDone/PopupDone.jsx";
/* The page kit -- shell, filter strip, list skin, dialog, buttons,
   states -- the same one every other list in this project reads. This
   tab was written against the classroom app's own cls-* vocabulary
   instead, which is why it looked like a different product: a bare
   <input> with no label, a Bootstrap utility class the project deleted,
   two inline styles, an eight-column table with no phone treatment, and
   Edit/Delete buttons in a cell rather than in the record's dialog.

   classroomApp.css is left alone -- the other five tabs are built on
   it -- this one simply stops reading it. */
import "../../../../components/Trainer/TrainerDashboard.css";
import "../../../../components/common/lms-pages.css";
import { confirm as confirmDialog } from "../../../../components/common/Confirm/confirm";

const PAGE_SIZE = 25;
const PAGE_SIZE_CHOICES = [25, 50, 100];
const DASH = "—";

const LabsTab = () => {
  const { hasPermission } = useAuth();
  const canAdd = hasPermission("list-classroom", ["add"], "lms");
  const canEdit = hasPermission("list-classroom", ["update"], "lms");
  const canDelete = hasPermission("list-classroom", ["delete"], "lms");

  const [labs, setLabs] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  /* A scroll batch, as against a fresh load. The table veils itself for
     `loading` -- right for a first load, wrong for adding rows under
     something being read. */
  const [loadingMore, setLoadingMore] = useState(false);
  const [uiSearchTerm, setUiSearchTerm] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  /* The record dialog, and the form dialog. null is closed; the form
     takes null for a new lab and the record for an edit. */
  const [details, setDetails] = useState(null);
  const [formModal, setFormModal] = useState(null);
  // A delete from the view popup is confirmed in it: the done panel
  // replaces its body and it closes itself after five seconds.
  const { done } = usePopupDone(Boolean(details));

  // Read before the fetch awaits anything: a reset mid-flight would turn
  // an append into a replace and snap the list back to its first page.
  const appendRef = useRef(false);
  const listEndRef = useRef(null);

  const load = useCallback(async () => {
    const isAppend = appendRef.current;
    if (isAppend) setLoadingMore(true);
    else setLoading(true);
    try {
      const result = await getLabsList(page, pageSize, search, statusFilter);
      const rows = result.data || [];
      setLabs((prev) => {
        if (!isAppend) return rows;
        /* The same row can arrive twice if one is added server-side
           between two batches and pushes the page boundary along. */
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...rows.filter((r) => !seen.has(r.id))];
      });
      setPagination(result.pagination);
    } catch (error) {
      console.error("Error loading labs:", error);
      toast.error("Failed to load labs and classrooms");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [page, pageSize, search, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  /* Typing searches; it does not wait for a button, and it does not fire
     a request per keystroke. */
  const runSearch = useDebouncedCallback((value) => {
    appendRef.current = false;
    setSearch(value.trim());
    setPage(1);
  });

  const totalRecords = pagination?.total ?? labs.length;
  const totalPages = pagination?.totalPages ?? 1;
  const hasMore = page < totalPages;

  /* The page is the scroller and the foot of the list reaching the
     viewport asks for the next batch. Declared below `hasMore`, which it
     reads. */
  useScrollLoadMore(
    listEndRef,
    () => {
      if (loading || loadingMore || !hasMore) return;
      appendRef.current = true;
      setPage((p) => p + 1);
    },
    [labs.length, page, totalPages, loading, loadingMore],
  );

  const refresh = useCallback(() => {
    appendRef.current = false;
    if (page === 1) load();
    else setPage(1);
  }, [page, load]);

  const handleDelete = async (lab) => {
    const confirm = await confirmDialog({
      title: "Delete Confirmation",
      text: `Are you sure you want to delete ${lab.classroomNo || "this room"}?`,
      confirmButtonText: "Yes, Delete it!",
      tone: 'danger',
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteClassroom(lab.id);
      // The view popup shows the done panel and closes itself after it.
      toast.success("Classroom deleted");
      refresh();
    } catch (error) {
      toast.error("Failed to delete classroom");
    }
  };

  /* Out of order is the one thing about a room you need at a glance, so
     it is a pill and not a word. The tints carry meaning and stay
     literal -- they are not the palette's to move. */
  const statusPill = (lab) =>
    lab.isActive === false ? (
      <span className="pg-pill pg-pill--bad">Out of order</span>
    ) : (
      <span className="pg-pill pg-pill--good">Available</span>
    );

  /* Five columns and no more. The description, the note behind an out of
     order room and both actions are in the dialog a row opens -- which is
     also what lets a phone hide everything but the name and the pill. */
  const headers = useMemo(
    () => [
      {
        title: "Lab / room",
        prop: "classroomNo",
        isSortable: true,
        cell: (row) => (
          <span className="pg-strong">{row.classroomNo || DASH}</span>
        ),
      },
      {
        title: "Branch",
        prop: "branchName",
        isSortable: true,
        cell: (row) =>
          row.branchName ? (
            <span>{row.branchName}</span>
          ) : (
            <span className="pg-none">{DASH}</span>
          ),
      },
      {
        title: "Floor",
        prop: "floor",
        isSortable: true,
        cell: (row) =>
          row.floor ? <span>{row.floor}</span> : <span className="pg-none">{DASH}</span>,
      },
      {
        title: "Seats",
        prop: "capacity",
        isSortable: true,
        cell: (row) =>
          row.capacity != null ? (
            <span>{row.capacity}</span>
          ) : (
            <span className="pg-none">{DASH}</span>
          ),
      },
      {
        title: "Status",
        prop: "isActive",
        isSortable: true,
        cell: (row) => statusPill(row),
      },
    ],
    [],
  );

  return (
    <div className="pg">
      <header className="pg-head">
        <div className="pg-head-main">
          <h1 className="pg-title">Labs &amp; classrooms</h1>
          <p className="pg-sub">
            Every room that can be booked, and whether it is out of order.
          </p>
        </div>

        {canAdd && (
          <div className="pg-head-actions">
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={() => setFormModal({ lab: null })}
            >
              Add lab
            </button>
          </div>
        )}
      </header>

      {/* Three controls, so it is a strip and not a sheet: the bottom sheet
          is for the six- and seven-control panels the bigger lists
          carry. */}
      <section className="pg-filters" aria-label="Filter labs">
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="lab-search">
              Search
            </label>
            <input
              id="lab-search"
              type="search"
              className="pg-input pg-input--search"
              placeholder="Search by room, description or floor..."
              value={uiSearchTerm}
              onChange={(e) => {
                setUiSearchTerm(e.target.value);
                runSearch(e.target.value);
              }}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="lab-status">
              Status
            </label>
            <select
              id="lab-status"
              className="pg-select"
              value={statusFilter}
              onChange={(e) => {
                appendRef.current = false;
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All</option>
              <option value="available">Available</option>
              <option value="outoforder">Out of order</option>
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="lab-rows">
              Rows per load
            </label>
            <select
              id="lab-rows"
              className="pg-select"
              value={pageSize}
              onChange={(e) => {
                appendRef.current = false;
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {PAGE_SIZE_CHOICES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {!loading && labs.length === 0 ? (
        <div className="pg-state">
          <h3 className="pg-state-title">
            {search || statusFilter ? "Nothing matched" : "No labs yet"}
          </h3>
          <p className="pg-state-text">
            {search
              ? `No room matches “${search}”.`
              : statusFilter
                ? "No room has that status."
                : "Rooms added here become bookable across the classroom app."}
          </p>
          {search && (
            <button
              type="button"
              className="pg-btn"
              onClick={() => {
                setUiSearchTerm("");
                runSearch.flush("");
              }}
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="td-list td-list--labs">
            <NewCustomDataTable
              data={labs}
              headers={headers}
              totalRecords={totalRecords}
              currentPage={page}
              recordsPerPage={pageSize}
              serverSide
              loading={loading}
              loadingMore={loadingMore}
              showToolbar={false}
              showPagination={false}
              showFooter={false}
              onRowClick={(row) => setDetails(row)}
              /* Every cell wraps its text in a span, so the default
                 ['TR','TD'] would leave the text unclickable. */
              validRowClickTagNames="*"
              rowProps={(row) => ({
                tabIndex: 0,
                role: "button",
                "aria-label": `Open ${row.classroomNo || "this room"}`,
                onKeyDown: (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setDetails(row);
                  }
                },
              })}
            />
          </div>

          {/* What the pager buttons used to say, said once. */}
          <div className="pg-scroll-foot" ref={listEndRef}>
            {labs.length < totalRecords ? (
              <>
                <span className="pg-scroll-count">
                  {labs.length} of {totalRecords}
                </span>
                <span
                  className={`pg-spinner${loadingMore ? "" : " pg-spinner--slot"}`}
                  aria-hidden="true"
                />
              </>
            ) : (
              totalRecords > 0 && (
                <span className="pg-scroll-count">
                  All {totalRecords} room{totalRecords === 1 ? "" : "s"} loaded
                </span>
              )
            )}
          </div>
        </>
      )}

      {/* ── The record dialog ──────────────────────────────────────────
          The room is what the record is about, so it is the title; the
          facts a column could not hold are here, and so are the two
          actions that used to sit in every row. Close is last and is the
          only filled button, so Delete is never the default under the
          cursor. */}
      {details && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lab-detail-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setDetails(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Lab / classroom</span>
                <h3 className="pg-modal-title" id="lab-detail-title">
                  {details.classroomNo || "Unnamed"}
                </h3>
                {details.branchName && (
                  <p className="pg-modal-sub">{details.branchName}</p>
                )}
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setDetails(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {done ? (
                <PopupDone message={done} onClose={() => setDetails(null)} />
              ) : (
                <>
                  <dl className="quiz-facts">
                    <div>
                      <dt>Status</dt>
                      <dd>{statusPill(details)}</dd>
                    </div>
                    <div>
                      <dt>Branch</dt>
                      <dd>{details.branchName || DASH}</dd>
                    </div>
                    <div>
                      <dt>Floor</dt>
                      <dd>{details.floor || DASH}</dd>
                    </div>
                    <div>
                      <dt>Seats</dt>
                      <dd>{details.capacity ?? DASH}</dd>
                    </div>
                  </dl>

                  {/* Each block is a .pgm-section, which is what carries the
                      22px between them -- the attendance popup's shape. Two
                      bare headings in a row would have run together. */}
                  <section className="pgm-section">
                    <h5 className="pgm-section-title">Description</h5>
                    {details.description ? (
                      <p className="lp-prose">{details.description}</p>
                    ) : (
                      <p className="pgm-empty">No description was recorded.</p>
                    )}
                  </section>

                  {/* Only a room that is out of order has a reason, and when
                      it is out of order the reason is the thing you opened
                      the record to read. */}
                  {details.isActive === false && (
                    <section className="pgm-section">
                      <h5 className="pgm-section-title">Why it is out of order</h5>
                      {details.reason ? (
                        <p className="lp-prose">{details.reason}</p>
                      ) : (
                        <p className="pgm-empty">No reason was given.</p>
                      )}
                    </section>
                  )}
                </>
              )}
            </div>

            {!done && (
              <footer className="pg-modal-foot">
                <div className="pgm-actions">
                  {canEdit && (
                    <button
                      type="button"
                      className="pgm-btn"
                      onClick={() => {
                        const lab = details;
                        setFormModal({ lab });
                      }}
                    >
                      Edit room
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      className="pgm-btn pgm-btn--danger"
                      onClick={() => {
                        const lab = details;
                        handleDelete(lab);
                      }}
                    >
                      Delete room
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  className="pg-btn pg-btn--main"
                  onClick={() => setDetails(null)}
                >
                  Close
                </button>
              </footer>
            )}
          </div>
        </div>
      )}

      {formModal && (
        <LabFormModal
          lab={formModal.lab}
          onClose={() => setFormModal(null)}
          onSaved={() => {
            // Saved: the form shows its done panel and closes itself; the
            // view popup behind it, which would show the old record, closes
            // now (the form is its sibling, not its child).
            setDetails(null);
            refresh();
          }}
        />
      )}
    </div>
  );
};

export default LabsTab;
